<template>
  <div class="media-manager mm relative">
    <spinner overlay="absolute" :show="isLoading" />
    <div class="mm__toolbar-wrapper relative z-level-1">
      <div
        class="mm__toolbar flex-column l:flex-row l:flex-between flex-wrap gap-m">
        <div class="buttons flex flex-wrap gap-m">
          <form-button
            v-if="
              localTaxonomyMode === 'folder' &&
              allowFolderNavigation &&
              allowFolderCreation &&
              !isInEmptyFolder
            "
            size="small"
            s-role="media-manager-button"
            icon="folder-plus"
            text="media.manager.createFolderCta"
            @click.prevent.stop="startCreateFolderProcess"></form-button>
          <form-button
            size="small"
            s-role="media-manager-button"
            icon="cloud-upload"
            text="media.manager.uploadButtonText"
            @click.prevent.stop="startUploadProcess"></form-button>
          <form-button
            v-show="currentItem != false"
            class="animate__fadeIn animate--fastest"
            size="small"
            theme="danger"
            s-role="media-manager-button"
            icon="cloud-upload"
            text="media.manager.deleteButtonText"
            @click.prevent.stop="deleteCurrentFile"></form-button>
          <form-button
            v-show="currentItem != false"
            class="animate__fadeIn animate--fastest"
            size="small"
            theme="gray-5"
            icon="pencil"
            s-role="media-manager-button"
            text="media.manager.renameButtonText"
            @click.prevent.stop="startRenameProcess"></form-button>
          <form-button
            v-show="currentItem != false"
            class="animate__fadeIn animate--fastest"
            size="small"
            theme="gray-5"
            icon="clipboard"
            s-role="media-manager-button"
            text="media.manager.copyUrlButtonText"
            @click.prevent.stop="copyCurrentFileUrl"></form-button>
          <form-button
            v-show="currentItem != false"
            class="animate__fadeIn animate--fastest"
            size="small"
            theme="gray-5"
            icon="cloud-download"
            s-role="media-manager-button"
            text="media.manager.downloadButtonText"
            @click.prevent.stop="downloadCurrentFile"></form-button>
          <form-button
            v-if="mediaSelect"
            v-show="currentItem != false"
            class="animate__fadeIn animate--fastest"
            size="small"
            theme="success"
            icon="check"
            s-role="media-manager-button"
            text="media.manager.selectButtonText"
            @click.prevent.stop="selectItem(currentItem)"></form-button>
        </div>
        <div class="localFilters flex flex-wrap flex-middle gap-m">
          <form-input
            v-model="textFilter"
            :label="false"
            size="small"
            placeholder="media.manager.searchFieldPlaceholder" />
          <slot
            name="filters"
            v-bind="{ mediaManager: this, localFilters }"></slot>
        </div>
      </div>
    </div>

    <div class="bar-loader-wrapper overflow-hidden">
      <bar-loader
        :fixed="false"
        style="width: 100%"
        :active="isAboutToLoad || mediaManager.isLoadingItems.value" />
    </div>

    <div
      ref="itemsContainer"
      class="mm__items relative"
      @dragenter.prevent.stop="handleFileDragEnter"
      @dragover.prevent.stop=""
      @dragleave.prevent.stop="handleFileDragLeave"
      @drop.prevent.stop="handleFileDrop">
      <div
        v-show="isDraggingFiles"
        style="
          box-shadow: 0 0 15px 2px rgba(0, 0, 0, 0.8) inset;
          pointer-events: none;
          background: rgba(0, 0, 0, 0.8);
        "
        class="z-level-5 drag-overlay size-parent c-bg flex-middle flex-center flex-column absolute-cover animate__fadeIn animate__faster">
        <div
          class="content width-expand size-parent flex-middle flex-center flex-column gap-l">
          <span class="text-2xl text-bold"
            >{{ tu("core.mediaManager.dropFilesToUpload") }}.</span
          >
          <icon icon="image" size="5" theme="bg" />
        </div>
      </div>
      <div class="mm__items-list flex gap-l flex-wrap relative">
        <div
          v-if="
            localTaxonomyMode === 'folder' &&
            canGoAFolderUp &&
            allowFolderNavigation
          "
          class="mm-item cursor-pointer"
          @click="goUpOneFolder">
          <div class="mm-item__preview">
            <div
              class="mm-item__preview-inner flex-middle flex-center relative">
              <icon icon="folder" size="5" class="absolute-center"></icon>
              <icon icon="redo" size="1.75" class="absolute-center"></icon>
            </div>
          </div>
          <div class="mm-item__meta">
            <div class="mm-item__name">
              <span>{{ tu("media.manager.backUpOneFolder") }}</span>
              {{ mediaManager.getParentFolder(finalFilters?.folder) }}
            </div>
          </div>
        </div>

        <div
          v-for="folder in folders"
          v-if="
            localTaxonomyMode === 'folder' &&
            folders &&
            folders.length > 0 &&
            allowFolderNavigation
          "
          class="mm-item cursor-pointer"
          @click="localFilters.folder = folder.path">
          <div class="mm-item__preview">
            <div class="mm-item__preview-inner flex-middle flex-center">
              <icon icon="folder" size="5"></icon>
            </div>
          </div>
          <div class="mm-item__meta">
            <div class="mm-item__name">
              {{ mediaManager.getFolderName(folder.path) }}
            </div>
          </div>
        </div>

        <div
          v-for="item of items"
          :key="item.id"
          v-click-outside="{
            handler: () => (currentItem = false),
            middleware: itemClickOutsideMiddleware,
          }"
          class="mm-item"
          :class="{ current: currentItem === item }"
          @dblclick="selectItem(item)"
          @click.prevent.stop="currentItem = item">
          <div class="mm-item__preview">
            <div
              class="mm-item__preview-inner"
              :style="`background-image: url('${item.thumbUrl}')`"></div>
          </div>
          <div class="mm-item__meta">
            <div class="mm-item__name">
              {{ item.fileName }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasMoreItems"
        class="load-more margin-xl-top flex flex-middle flex-center margin-auto with-expand">
        <form-button
          theme="success"
          text="load more"
          @click.prevent="mediaManager.loadMore()"></form-button>
      </div>
      <div
        v-if="
          !items ||
          (items.length < 1 &&
            (!currentFolder || currentFolder !== lastCreatedFolder))
        "
        class="no-items-found flex flex-middle flex-center margin-auto with-expand">
        <h3>{{ translate("media.manager.noItems") }}</h3>
      </div>
      <div
        v-if="currentFolder === lastCreatedFolder && isInEmptyFolder"
        class="no-items-found flex flex-middle flex-center margin-auto with-expand">
        <h3>
          <icon-text theme="warning" icon="info" class="c-danger">
            <span class="c-text">{{
              tu("media.manager.virtualFolderRequiresFilesExplain")
            }}</span>
          </icon-text>
        </h3>
      </div>
    </div>
    <div class="mm__footer-wrapper">
      <div class="mm__footer flex flex-wrap gap-m">
        <form-input
          v-model="currentFolder"
          :disabled="
            !enableManualFolderSelection || localTaxonomyMode !== 'folder'
          "
          icon="folder"
          :label="false"
          size="small"
          placeholder="media.manager.folderFieldPlaceholder" />
        <div class="flex-row flex-middle">
          <form-input
            v-if="allowTaxonomyModeSwitch"
            v-model="localTaxonomyMode"
            :icon="isRtl ? 'folder' : 'basket'"
            :icon-end="isRtl ? 'basket' : 'folder'"
            :label-position="'none'"
            :values="['bucket', 'folder']"
            type="toggle"
            :label="false"
            size="small" />
        </div>

        <div v-if="mediaManagerHasError">
          <div class="flex gap-s">
            <icon icon="warning-triangle" theme="danger" />
            <span>{{ translate("core.genericError") }}</span>
          </div>
        </div>

        <span>&nbsp;</span>
      </div>
    </div>

    <file-input
      ref="fileUploadField"
      v-model="uploadFiles"
      class="hidden"
      multiple="true"
      label="file upload" />
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import useMediaManager from "@/client/extensions/composition/useMediaManager";
import axios from "axios";

export default {
  props: {
    /**
     * Does this UI allow selecting media?
     * If so, proper UI is provided, and media:item-selected event emitted
     */
    mediaSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * Filters for the media. SaffronList filters.
     */
    filters: {
      type: Object,
      default: {},
    },
    /**
     * Data to be added while uploading media
     */
    uploadData: {
      type: Object,
      default: {},
    },
    /**
     * Max width for images for client side media resizing
     */
    maxImageWidth: {
      type: Number,
      default: 1920,
    },
    /**
     * Max height for images for client side media resizing
     */
    maxImageHeight: {
      type: Number,
      default: 1200,
    },
    /**
     * Display/taxonomy mode. Should we use virtual folders, or a bucket
     */
    taxonomyMode: {
      type: String,
      default: "folder", // 'folder'/'bucket'
    },
    /**
     * Root folder while using taxonomyMode folder
     * UI will not show parents for this folder,
     * so it acts as root for this instance
     */
    rootFolder: {
      type: [String, Boolean],
      default: false,
    },
    /**
     * Allow virtual folder creation
     */
    allowFolderCreation: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable field to manually set folder to view
     */
    enableManualFolderSelection: {
      type: Boolean,
      default: false,
    },
    /**
     * Allow switching from folder to bucket taxonomy (and back)
     */
    allowTaxonomyModeSwitch: {
      type: Boolean,
      default: true,
    },
    /**
     * Allow navigating withing folders (going deeper, and up to the root)
     */
    allowFolderNavigation: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["media:item-selected"],
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    const mediaManager = useMediaManager(props, {});
    return { asyncOps, asyncOpsReady, asyncStatus, mediaManager };
  },
  data: function () {
    let localFilters = {};
    if (this.rootFolder) {
      localFilters.folder = this.rootFolder;
    }

    if (
      !this.rootFolder &&
      this.taxonomyMode === "folder" &&
      typeof this.filters.folder === "undefined"
    ) {
      localFilters.folder = this.$store.getters["mediaManager/lastFolderPath"];
    }

    return {
      applyFilterDebounce: false,
      isDraggingFiles: false,
      isAboutToLoad: false,
      textFilter: "",
      textFilterDebounce: false,
      filterDebounce: false,
      isLoading: false,
      uploadFiles: false,
      localTaxonomyMode: this.taxonomyMode,
      mediaManagerHasError: this.mediaManager.hasError,
      items: this.mediaManager.items,
      folders: this.mediaManager.folders,
      currentItem: false,
      localFilters: localFilters,
      lastCreatedFolder: false,
    };
  },
  computed: {
    isInEmptyFolder() {
      return !this.items || this.items.length < 1;
    },
    finalFilters: {
      get() {
        return { ...this.filters, ...this.localFilters };
      },
      set(newVal) {
        this.localFilters = newVal;
        return true;
      },
    },
    currentFolder: {
      get() {
        return this?.finalFilters?.folder;
      },
      set(newVal) {
        this.localFilters.folder = newVal;
      },
    },
    hasMoreItems() {
      return (
        Number(this.mediaManager.totalItems.value) > Number(this.items.length)
      );
    },
    hasSelectedItem() {
      return !!this.currentItem;
    },
    canGoAFolderUp() {
      let isRoot =
        typeof this.finalFilters?.folder === "string" &&
        (this.finalFilters?.folder === "" || this.finalFilters?.folder === "/");
      if (isRoot) {
        return false;
      }

      let parentFolder = this.mediaManager.getParentFolder(
        this.finalFilters.folder
      );
      if (!parentFolder) {
        return false;
      }

      // this is redundant but it is here in order to clarify all aspects of this logic
      return this.finalFilters.folder !== this.rootFolder;
    },
  },
  watch: {
    uploadFiles(newVal) {
      if (!newVal || newVal.length < 1) {
        return true;
      }
      this.uploadFilesByState();
    },
    textFilter(newVal) {
      this.isAboutToLoad = true;
      clearTimeout(this.textFilterDebounce);
      this.textFilterDebounce = utilities.setClientTimeout(() => {
        this.setCurrentTextFilterValueAsMediaFilter();
        this.isAboutToLoad = false;
      }, 350);
    },
    finalFilters: {
      handler(newVal, oldVal) {
        if (typeof newVal.folder !== "undefined") {
          let path = typeof newVal.folder === "string" ? newVal.folder : "/";
          if (path === "") {
            path = "/";
          }
          this.$store.commit("mediaManager/setLastFolderPath", path);
        }

        clearTimeout(this.applyFilterDebounce);

        this.applyFilterDebounce = utilities.setClientTimeout(async () => {
          await this.sg("user.initialFetchCompletePromise");
          this.applyFilters(this.finalFilters).then(() => {
            this.mediaManager.reloadItems();
          });
        }, 200);
      },
      deep: true,
      immediate: true,
    },
    localTaxonomyMode(newVal) {
      if (newVal === "folder") {
        if (!this.rootFolder || this.rootFolder === "") {
          this.currentFolder = "/";
        } else {
          this.currentFolder = this.rootFolder;
        }
      }

      if (newVal === "bucket") {
        this.currentFolder = this.rootFolder;
      }
    },
  },
  async mounted() {},
  methods: {
    async startCreateFolderProcess() {
      try {
        let name = await this.$s.ui.modal.prompt(
          "media.manager.createFolderPromptText",
          "media.manager.createFolderPromptTitle"
        );
        name = name.replaceAll(/[^a-zA-Z_-]+/g, "_");
        if (this.currentFolder.endsWith("/")) {
          this.currentFolder = this.currentFolder + name;
        } else {
          this.currentFolder = this.currentFolder + "/" + name;
        }

        this.lastCreatedFolder = this.currentFolder;
        this.$s.ui.notification("media.manager.folderCreated", "success");
      } catch (e) {
        this.$s.ui.notification("media.manager.folderCreationAborted");
      }
    },
    handleFileDragEnter(event) {
      let dropDiv = this.$refs.itemsContainer;
      if (!dropDiv.contains(event.relatedTarget)) {
        this.isDraggingFiles = true;
      }
    },
    handleFileDragLeave() {
      let dropDiv = this.$refs.itemsContainer;
      if (!dropDiv.contains(event.relatedTarget)) {
        this.isDraggingFiles = false;
      }
    },
    handleFileDrop(event) {
      if (!event?.dataTransfer?.files) {
        return;
      }
      try {
        this.$refs.fileUploadField.$el.files = event.dataTransfer.files;
        this.uploadFiles = event.dataTransfer.files;
        this.isDraggingFiles = false;
      } catch (e) {}
    },
    selectItem(item) {
      if (this.mediaSelect) {
        this.$emit("media:item-selected", { item });
        this.currentItem = null;
      }
    },
    async applyFilters(oldFilters) {
      return new Promise((fulfil) => {
        this.isAboutToLoad = true;
        clearTimeout(this.filterDebounce);
        this.filterDebounce = utilities.setClientTimeout(() => {
          // clear all existing finalFilters
          if (oldFilters) {
            for (const key of Object.keys(oldFilters)) {
              this.mediaManager.unsetFilter(key);
            }
          }

          // set new finalFilters by state
          for (const [key, value] of Object.entries(this.finalFilters)) {
            let hasVal = value && value !== "";
            hasVal
              ? this.mediaManager.setFilter(key, value)
              : this.mediaManager.unsetFilter(key);
          }

          // do not forget the text filter - it is "special" at the moment
          let hasVal = this.textFilter && this.textFilter !== "";
          hasVal
            ? this.mediaManager.setFilter("fileName", this.textFilter)
            : this.mediaManager.unsetFilter("fileName");
          this.isAboutToLoad = false;
          fulfil();
        }, 100);
      });
    },
    setCurrentTextFilterValueAsMediaFilter() {
      let hasVal = this.textFilter && this.textFilter !== "";
      if (hasVal) {
        this.localFilters.fileName = this.textFilter;
      } else {
        delete this.localFilters.fileName;
      }
    },
    itemClickOutsideMiddleware(event, el) {
      if (utilities.isSSR()) {
        return false;
      }

      let parent = event.target.closest("button");
      let attr = event.target.getAttribute("s-role");
      let parentAttr = parent ? parent.getAttribute("s-role") : false;
      let isIgnore =
        attr === "media-manager-button" ||
        parentAttr === "media-manager-button";

      return !isIgnore;
    },
    startUploadProcess() {
      try {
        this.$refs.fileUploadField.promptFileSelection();
      } catch (e) {
        warn("failed to focus file input", e);
      }
    },
    async uploadFilesByState() {
      let hasError = false;

      // basic validate the files
      for (const file of this.uploadFiles) {
        if (file.size / 1000000 > config.mediaManager.maxFileSizeMB) {
          this.$s.ui.notification(
            "media.manager.fileTooLargeNotification",
            "error",
            {
              contentParams: { maxSizeMB: config.mediaManager.maxFileSizeMB },
            }
          );
          return false;
        }
      }

      this.isLoading = true;
      for (const file of this.uploadFiles) {
        let uploadCandidate = file;
        if (this.mediaManager.isImage(file)) {
          uploadCandidate = await this.mediaManager.resizeImage(
            file,
            this.maxImageWidth,
            this.maxImageHeight
          );
        }
        let finalUploadData = { ...this.uploadData };
        if (this.currentFolder) {
          finalUploadData.folder = this.currentFolder;
        }

        const uploadResult = await this.mediaManager.upload(uploadCandidate, {
          data: finalUploadData,
        });

        if (!uploadResult) {
          hasError = true;
        }
      }

      if (hasError) {
        this.$s.ui.notification("media.manager.uploadErrorOccurred", "error");
        this.isLoading = false;
        return;
      }

      this.asyncOps.clearAllRequestDataFromCache();
      await this.mediaManager.reloadItems();
      this.isLoading = false;
    },
    deleteCurrentFile() {
      const id = this.currentItem.id;
      this.$s.ui.modal
        .confirm()
        .then(async () => {
          this.isLoading = true;
          if (!(await this.mediaManager.delete(id))) {
            this.isLoading = false;
            return this.$s.ui.notification("core.errorGeneric");
          }

          this.mediaManager.reloadItems();
          this.$s.ui.notification("core.successGeneric", "success");
          this.isLoading = false;
        })
        .catch((e) => {
          this.$s.ui.notification("core.actionCanceled");
          this.isLoading = false;
        });
    },
    async startRenameProcess() {
      let res = false;
      let id = this.currentItem.id;
      let splitName = this.currentItem.fileName.split(".");
      let oldName = splitName[0];
      let extension = splitName[1];
      try {
        res = await this.$s.ui.modal.prompt(
          null,
          "media.manager.renamePromptTitle",
          {
            initialValue: oldName,
            field: {
              name: "newName",
              label: "media.manager.promptRenameFieldLabel",
            },
          }
        );
      } catch (e) {
        return true;
      }

      //notice  the modal has deselected our currentItem, as it is a click outside
      if (!res || res === "" || res.length < 3) {
        this.$s.ui.notification(
          "media.manager.renameErrorInvalidInput",
          "error"
        );
        return false;
      }
      const newName = res + "." + extension;
      this.isLoading = true;
      let result = await this.mediaManager.rename(id, newName);

      await this.mediaManager.reload();
      this.isLoading = false;

      if (!result) {
        this.$s.ui.notification("core.errorGeneric", "error");
        return;
      }

      this.$s.ui.notification("core.successGeneric", "success");
    },
    async copyCurrentFileUrl() {
      await navigator.clipboard.writeText(this.currentItem.url);
      this.$s.ui.notification("media.manager.urlCopied");
    },
    async downloadCurrentFile() {
      const url = this.currentItem.url;
      // only show spinner if this actually takes a while. this is cleaned up in the end
      let timeout = setTimeout(() => {
        this.isLoading = true;
      }, 100);

      try {
        const response = await axios.get(url, { responseType: "blob" });
        const blob = response.data;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download =
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "downloaded-file";
        link.click();

        URL.revokeObjectURL(link.href);
      } catch (e) {
        // failure is extremely unlikely, we just need it so that the isLoading goes back to false no matter what.
      }
      clearTimeout(timeout);
      this.isLoading = false;
    },
    goUpOneFolder() {
      this.localFilters.folder = this.mediaManager.getParentFolder(
        this.finalFilters?.folder
      );
      return true;
    },
  },
};
</script>

